import { Task } from "./utility";
import { Classification } from "./classification";
import { Contract } from "./contract";
import { MfvClaim } from "./mfv-claim";
import { CognitoUser } from "./cognito-user";

export enum FnolRequestStatus {
  // NEW
  TODO = 0,
  FNOL_DATA_CONFIRMED = 1,

  // IN PROGRRESS
  INVOICE_PARSED_CONFIRMED = 2,
  VAT_AND_DISCOUNT_SET = 3,
  CONTRACTS_ASSIGNED = 4,
  INVOICE_ITEMS_TO_CONTRACT_ASSIGNED = 5,
  TREATMENT_VALIDATED = 6,
  CLASSIFICATIONS_ASSIGNED = 7,
  CLASSIFICATIONS_VALIDATED = 8,
  USED_UP_FRANCHISE_FILLED = 9,
  PAYMENT_CALCULATED = 10,

  // DONE
  NO_PROCESSING = 98,
  DONE = 99, // left some space for future statuses
}

export enum FnolRequestStatusFilter {
  ALL = 'ALL',
  TODO = 0,
  IN_PROGRESS = 1,
  DONE = 99,
}

export class FnolDetail {
  fnolFirstName: string = '';
  fnolLastName: string = '';
  fnolEmail: string = '';
  fnolPhoneNumber: string = '';
  fnolPreferredChannel: string = 'email'; // Example default
  fnolLang: string = 'en';              // Example default
  fnolIban: string = '';
  fnolPetName: string = '';
  fnolConditionPreviouslyTreated: boolean = false;
  fnolDateConditionLastTreated: string = '';
  fnolDateConditionFirstTreated: string = '';
  fnolDateConditionNoticed: string = '';
  fnolDateOfTreatment: string = '';
  fnolTreatedInSwitzerland: boolean = false;
  fnolInvoicedAmount: number = 0;
  fnolTreatmentFinished: boolean = false;
  fnolThirdPartyFault: boolean = false;
  fnolAdditionalInformation: string = '';
  fnolDiagnoses: Array<{ id: string; category: string }> = [];
  fnolWhitelabelFlavor: string = '';
  base64FnolSummaryDoc: string | null = null;


  constructor(data?: Partial<FnolDetail>) {
    Object.assign(this, data);
  }

  static extractFromFnolRequest(fnolRequest: FnolRequest): FnolDetail {
    return new FnolDetail({
      fnolFirstName: fnolRequest.fnolFirstName,
      fnolLastName: fnolRequest.fnolLastName,
      fnolEmail: fnolRequest.fnolEmail,
      fnolPhoneNumber: fnolRequest.fnolPhoneNumber,
      fnolPreferredChannel: fnolRequest.fnolPreferredChannel,
      fnolLang: fnolRequest.fnolLang,
      fnolIban: fnolRequest.fnolIban,
      fnolPetName: fnolRequest.fnolPetName,
      fnolConditionPreviouslyTreated: fnolRequest.fnolConditionPreviouslyTreated,
      fnolDateConditionLastTreated: fnolRequest.fnolDateConditionLastTreated,
      fnolDateConditionFirstTreated: fnolRequest.fnolDateConditionFirstTreated,
      fnolDateConditionNoticed: fnolRequest.fnolDateConditionNoticed,
      fnolDateOfTreatment: fnolRequest.fnolDateOfTreatment,
      fnolTreatedInSwitzerland: fnolRequest.fnolTreatedInSwitzerland,
      fnolInvoicedAmount: fnolRequest.fnolInvoicedAmount,
      fnolTreatmentFinished: fnolRequest.fnolTreatmentFinished,
      fnolThirdPartyFault: fnolRequest.fnolThirdPartyFault,
      fnolAdditionalInformation: fnolRequest.fnolAdditionalInformation,
      fnolDiagnoses: fnolRequest.fnolDiagnoses,
      fnolWhitelabelFlavor: fnolRequest.fnolWhitelabelFlavor
    });
  }
}

export interface Treatment {
  petName: string;
  date: string;
  diagnosis: string;
  positions: Array<Position>;
  subTotal?: number;
  contract: Contract | null;
  comments: Array<TreatmentCheckComment>;
  petNameValidationStatus?: InvoiceItemsCoverageCheckStatus;
  dateValidationStatus?: InvoiceItemsCoverageCheckStatus;
  invoiceRecipientValidationStatus?: InvoiceItemsCoverageCheckStatus;
  hasOverdueInvoices?: boolean;
  dateOverdueInvoicesChecked?: string;
  // mfvReserveClaimID?: string;
}

export interface TreatmentCheckComment {
  fieldName: string;
  comment: string;
  addedBy: 'system' | 'user';
}

export interface Position {
  name: string;
  quantity: string;
  vatPercentage: string;
  amount: number;
  classification: Partial<Classification> | null | undefined;
}

export interface PositionUI extends Position {
  copySource: boolean;
  copyTarget: boolean;
}

export interface ParsedInvoiceData {
  invoiceMetadata: {
    issuerName: string;
    issuerAddress: string;
    recipientName: string;
    recipientAddress: string;
    invoiceNumber: string;
    invoiceDate: string;
    language: string;
  },
  recognizedVet?: {
    value: boolean,
    needToSetReason: boolean,
  },
  treatments: Array<Treatment>,
  sumWithoutVat: number;
  sumWithVat: number;
  calculatedSum: number;
  calculationInformation?: VatAndDiscountCalculationInformation;
}

export interface FnolRequest extends FnolDetail {
  id: string;
  notifier: string;
  notificationMedium: string;
  notificationDate: string;
  ocrExtractionId: string;
  parsedInvoiceData: ParsedInvoiceData;
  processingStatus: FnolRequestStatus;
  processingAssignee: string;
  tasks: Task[];
  priority: number;
  invoiceDocument: {
    s3Id: string
  };
  additionalDocuments: {
    s3Id: string
  }[];
  mfvClaims?: Array<MfvClaim>;
  logsUploadDocumentsToMfv: string;
  labels?: Array<TasksLabels>;
  mfvReserveClaimContractID: string;
  mfvReserveClaimID: string;
  notes?: string;
  notesAgent?: string;
  initialReserveData?: InitialReserveData;
  parentRequestId?: string;
  clonedRequestIds?: string[];
  multipleClaimsRef?: {
    id: string,
    pet_name: string,
    notificationDate: string
  }[];
  reserveDuplicateIDs?: string[];
  automaticEmailSentAt?: string; // ISO timestamp when automatic email was successfully sent
}

export interface InitialReserveData {
  fullContractNumber: string
  contractID: number
  type: string
  claimDate: string
  amount: number
  courseOfEvents: string
  claimID?: number
  claimNumber?: string
}

export interface CreateMfvClaimResponseData {
  claimID: number
  claimNumber: string
  reserveDuplicateId?: string
}


export enum TasksLabels {
  SIMPLE_CLAIM = 'simple_claim',
  MULTIPLE_CLAIMS = 'multiple_claims',
  WAITING = 'waiting',
  SIMPEGO = 'simpego',
  AGENT = 'agent',
  POTENTIAL_DUPLICATE = 'potential_duplicate',
}

export interface ExtractedDocument {
  inQueue: boolean;
  queueStatus: string;
  ocrError: boolean;
  ocrErrorMessage: string;
  parsedInvoiceData?: ParsedInvoiceData;
  errorMessage: string;
}

export enum TreatmentVerificationStatus {
  OK = 'OK',
  WARNING = 'WARNING',
  ERROR = 'ERROR'
}

export interface TreatmentUI extends Treatment {
  expanded: boolean;
  positions: Array<PositionUI>;
}

export enum InvoiceItemsCoverageCheckStatus {
  NOT_NECESSARY = 'not_necessary',
  OPEN = 'open',
  PERFORMED = 'performed',
}

export interface VatAndDiscountCalculationInformation {
  vatContainedInPositions: boolean;
  totalInclVat: number;
  totalExclVat: number;
  vatAmountToDistribute: number;
  vatPercentageToDistribute: number;
  applicableDiscountAmount: number;
  forexFactor?: number;
}

export interface FnolRequestListItem extends FnolRequest {
  taskLabels: Map<TasksLabels, boolean>;
  hasUnclosedTasks: boolean;
  hasReservedClaims: boolean;
  statusString: string;
  assigneeUser?: CognitoUser;
}




export enum MfvDocumentKind {
  ASSESSMENT = 'assessment',
  BANK_DOCUMENT = 'bank_document',
  CHATBOT_UPLOAD = 'chatbot_upload',
  CONFIDENTIAL_CORRESPONDENCE = 'confidential_correspondence',
  CORRESPONDENCE = 'correspondence',
  DAMAGE_REPORT = 'damage_report',
  ESTIMATE_OF_COSTS = 'estimate_of_costs',
  INTERNAL = 'internal',
  INVOICE_REFUND = 'invoice_refund',
  MEDICAL_FILE = 'medical_file',
  OFFICIAL_FILE = 'official_file',
  OTHER_DAMAGE_DOCUMENT = 'other_damage_document',
  PHOTO = 'photo',
  RECOURSE_CLAIM = 'recourse_claim',
}